import { UpdateDeliveryDto } from '@repo/types';
import { useState } from 'react';
import { HiPencil } from 'react-icons/hi';
import { LuMoveUpRight } from 'react-icons/lu';
import { useNavigate, useParams } from 'react-router-dom';
import DeliveryEditModal from './DeliveryEditModal';
import { getDeliveryStateLabel } from '../utils/deliveryStateLabels';
import { getDeliveryStateBadgeVariant } from '../utils/getDeliveryStateBadgeVariant';
import { DetailPageLayout } from '@/components/Layout/DetailPageLayout';
import { Badge, Button, Card, InfoItem } from '@/components/ui';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { useDeliveryDetail, useUpdateDelivery } from '@/hooks/useDelivery';
import { formatDateTime } from '@/utils/dateTime';
import { getErrorMessage } from '@/utils/errorHandler';
import { getNationalFormatPhoneNumber } from '@/utils/get-national-format-phone-number';

export default function DeliveryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const deliveryId = id ? Number(id) : NaN;
  const resolvedDeliveryId = Number.isNaN(deliveryId) ? null : deliveryId;

  const { data, isLoading, isError, error, refetch } = useDeliveryDetail(resolvedDeliveryId);
  const updateDelivery = useUpdateDelivery(resolvedDeliveryId);

  const [showDeliveryEditModal, setShowDeliveryEditModal] = useState(false);
  const [resultModal, setResultModal] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const formatPhone = (phone: string | null | undefined) => {
    const trimmed = (phone ?? '').trim();
    return trimmed ? getNationalFormatPhoneNumber(trimmed) : '-';
  };

  const handleMoveToAccountDetailPage = () => {
    if (!data) return;
    void navigate(`/dashboard/users/${data.accountId}`);
  };

  const handleMoveToProductDetailPage = (productId: number) => {
    void navigate(`/dashboard/products/${productId}`, {
      state: { from: `/dashboard/delivery/${deliveryId}` },
    });
  };

  const handleDeliveryEditSave = async (patch: UpdateDeliveryDto) => {
    try {
      await updateDelivery.mutateAsync(patch);
      setShowDeliveryEditModal(false);
      setResultModal({ type: 'success', message: '배송 정보가 저장되었습니다.' });
    } catch (mutationError) {
      setResultModal({
        type: 'error',
        message: getErrorMessage(mutationError),
      });
      throw mutationError;
    }
  };

  const addressLine = data
    ? [data.zipcode && `(${data.zipcode})`, data.address1, data.address2].filter(Boolean).join(' ')
    : '-';

  return (
    <DetailPageLayout
      title="배송 상세 정보"
      subtitle={data ? `배송 ID: ${data.id} · 주문서 ID: ${data.orderSheetId}` : undefined}
      isLoading={isLoading}
      isError={isError}
      errorMessage={error instanceof Error ? error.message : undefined}
      onRetry={refetch}>
      {data && (
        <>
          {showDeliveryEditModal && (
            <DeliveryEditModal
              onClose={() => setShowDeliveryEditModal(false)}
              initial={{
                state: data.state,
                carrier: data.carrier ?? '',
                trackingNumber: data.trackingNumber ?? '',
              }}
              onSave={handleDeliveryEditSave}
            />
          )}

          {resultModal && (
            <ConfirmModal
              isOpen={true}
              onClose={() => setResultModal(null)}
              type={resultModal.type}
              title={resultModal.type === 'success' ? '저장 완료' : '저장 실패'}
              message={resultModal.message}
              buttons={[
                {
                  label: '확인',
                  onClick: () => setResultModal(null),
                  variant: 'primary',
                },
              ]}
            />
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card
              title="주문 정보"
              actions={
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleMoveToAccountDetailPage}
                  rightIcon={<LuMoveUpRight />}>
                  학습자 상세로 이동
                </Button>
              }>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InfoItem label="주문자 ID" value={data.accountId} />
                <InfoItem label="이름" value={data.buyerName} />
                <InfoItem label="이메일" value={data.buyerEmail} />
                <InfoItem label="전화번호" value={formatPhone(data.buyerPhone)} />
                <InfoItem label="주문 일시" value={formatDateTime(data.orderedAt)} />
              </div>
            </Card>

            <Card title="수령인 정보">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InfoItem label="수령인" value={data.receiverName} />
                <InfoItem label="수령인 전화번호" value={formatPhone(data.receiverPhone)} />
              </div>
            </Card>

            <Card
              title="배송 정보"
              actions={
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowDeliveryEditModal(true)}
                  leftIcon={<HiPencil />}
                  disabled={updateDelivery.isPending}>
                  수정하기
                </Button>
              }>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="col-span-2">
                  <InfoItem label="주소" value={addressLine || '-'} />
                </div>
                <InfoItem
                  label="배송 상태"
                  value={
                    <Badge variant={getDeliveryStateBadgeVariant(data.state)} size="sm">
                      {getDeliveryStateLabel(data.state)}
                    </Badge>
                  }
                />
                <InfoItem label="택배사" value={data.carrier || '-'} />
                <InfoItem label="운송장 번호" value={data.trackingNumber || '-'} />
                <div className="col-span-2">
                  <InfoItem label="메모" value={data.memo || '-'} />
                </div>
              </div>
            </Card>

            <Card title="상품 정보">
              {data.products.length === 0 ? (
                <p className="text-sm text-slate-500">연결된 상품이 없습니다.</p>
              ) : (
                <div className="space-y-6">
                  {data.products.map((product) => (
                    <div key={product.productId} className="space-y-4">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-slate-900">{product.title}</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMoveToProductDetailPage(product.productId)}
                          rightIcon={<LuMoveUpRight />}>
                          상품 상세로 이동
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <InfoItem label="상품 ID" value={product.productId} />
                        <InfoItem label="상품 이름" value={product.title} />
                        <InfoItem label="상품 원가" value={`${product.originalPrice.toLocaleString()}원`} />
                        <InfoItem
                          label="결제 금액"
                          value={`${(product.originalPrice - product.discount).toLocaleString()}원`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </DetailPageLayout>
  );
}
